import { useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { adminFilesApi } from '../api/sales';
import { useToast } from './Toast';

export interface ImageUploadFieldProps {
  label?: string;
  value?: string | null;
  onChange: (url: string) => void;
  folder?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  aspectRatioText?: string;
}

export function ImageUploadField({
  label,
  value,
  onChange,
  folder = 'general',
  hint,
  required = false,
  disabled = false,
  className = '',
  aspectRatioText,
}: ImageUploadFieldProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = async (file: File) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel dosyası seçin (PNG, JPG, WebP, SVG).');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Görsel boyutu en fazla 10 MB olabilir.');
      return;
    }

    setIsUploading(true);
    try {
      const res = await adminFilesApi.uploadImage(file, folder);
      if (res?.url) {
        onChange(res.url);
        toast.success('Görsel başarıyla yüklendi.');
      } else {
        toast.error('Görsel URL bilgisi alınamadı.');
      }
    } catch (err) {
      toast.fromError(err, 'Görsel yüklenirken bir hata oluştu.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void handleFile(file);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled || isUploading) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFile(file);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled && !isUploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <div className={className}>
      {label ? (
        <label className="mb-1.5 flex items-center justify-between text-xs font-medium text-ink-700">
          <span>
            {label} {required ? <span className="text-red-500">*</span> : null}
          </span>
          {aspectRatioText ? <span className="text-[11px] text-ink-400 font-normal">{aspectRatioText}</span> : null}
        </label>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
        className="hidden"
        onChange={handleInputChange}
        disabled={disabled || isUploading}
      />

      {value ? (
        <div className="relative group flex items-center gap-3.5 rounded-xl border border-ink-200 bg-ink-50/50 p-2.5 transition hover:border-ink-300">
          <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-lg border border-ink-200 bg-white">
            <img src={value} alt="Önizleme" className="h-full w-full object-cover" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-ink-800" title={value}>
              {value.split('/').pop() || 'Yüklendi'}
            </p>
            <p className="mt-0.5 text-[11px] text-emerald-600 font-medium">✓ Görsel hazır</p>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={disabled || isUploading}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 hover:text-ink-900 transition disabled:opacity-50"
            >
              Değiştir
            </button>
            <button
              type="button"
              disabled={disabled || isUploading}
              onClick={() => onChange('')}
              className="rounded-lg p-1.5 text-ink-400 hover:bg-rose-50 hover:text-rose-600 transition disabled:opacity-50"
              title="Görseli kaldır"
              aria-label="Görseli kaldır"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => {
            if (!disabled && !isUploading) {
              fileInputRef.current?.click();
            }
          }}
          className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition ${
            isDragOver
              ? 'border-brand-500 bg-brand-50/50 text-brand-700'
              : 'border-ink-200 hover:border-brand-300 hover:bg-ink-50/50 text-ink-500'
          } ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center gap-2 py-1">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
              <span className="text-xs font-medium text-brand-700">Görsel yükleniyor...</span>
            </div>
          ) : (
            <>
              <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-ink-100/70 text-ink-600">
                {isDragOver ? <Upload className="h-5 w-5 animate-bounce" /> : <ImageIcon className="h-5 w-5" />}
              </div>
              <p className="text-xs font-medium text-ink-700">
                <span className="text-brand-600 hover:underline">Görsel seçin</span> veya buraya sürükleyin
              </p>
              <p className="mt-1 text-[11px] text-ink-400">PNG, JPG, WebP veya SVG (Maks. 10 MB)</p>
            </>
          )}
        </div>
      )}

      {hint ? <p className="mt-1 text-[11px] text-ink-400">{hint}</p> : null}
    </div>
  );
}
